import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import RouterLink from 'components/RouterLink';
import { ThemeProvider } from 'react-css-themr';
import { isEmpty, uniq } from 'lodash';
import Card from 'components/Card';
import CardTitle from 'components/CardTitle';
import { ResourceDetailsCardElement } from 'components/ResourceCardElements';
import theme from 'styles/resourceDetailsCard';
import sectionTheme from 'styles/detailsSection.scss';
import styles from 'phaseDetailsCard.scss';
import detailsCardElement from 'styles/detailsCardElement.scss';
import BreakLine from 'components/BreakLine';
import DetailsAddress from 'components/DetailsAddress';
import EmailLink from 'components/EmailLink';
import PhoneLink from 'components/PhoneLink';
import PurchaseOrders from 'components/PurchaseOrders';
import cx from 'classnames';
import PhaseType from 'types/phase';
import purchaseOrderShape from 'types/purchaseOrder';
import { getRole } from '../../../../../../../reducers';
import { can } from '../../../../../../../services';
import { urls } from 'helpers/url';
import { vendorDisplayName } from '../../../../../../../utils';
import { MILLWORK_SURFACES } from 'config/constants';

const detailsTheme = {
  RTCard: theme,
  ResourceDetailsCE: detailsCardElement,
};

const emptyFieldValue = '----';

const sectionTitle = cx([sectionTheme.sectionTitle, sectionTheme.mediumTitle]);

const determineBacksplashFieldName = (key) => {
  switch (key) {
    case 'tile_stone_location':
      return 'Tile/Stone Location';
    case 'stone_size':
      return 'Backsplash Tile Or Stone Size';
    case 'backsplash_job_type':
      return 'Backsplash/Wall Job Type';
    case 'stone_style':
      return 'Backsplash Tile Or Stone Style';
    case 'stone_pattern_type':
      return 'Backsplash Tile Or Stone Pattern Type';
    default:
      return key.replace(/_/g, ' ');
  }
};

const determineSurfaceDisplayValue = val => val || emptyFieldValue;

const renderAddtlSurfaceInfo = materials => materials.map((material, index) => (<Fragment
  key={material.modelId}
>
  <hr className={theme.dividerLine} />
  <BreakLine />
  <h9>Material {index + 1}</h9>
  <BreakLine />
  <ResourceDetailsCardElement
    title="Item Number" value={
      <a style={{ color: 'blue' }} href={urls.lowesItem(material.itemNumber)} target="_blank" rel="noopener noreferrer">{material.itemNumber}</a>}
  />
  <ResourceDetailsCardElement title="model id" value={material.modelId} />
  <ResourceDetailsCardElement title="description" value={material.materialDesc} />
</Fragment>));

const renderRfInfo = (rfLaborComments) => {
  const allTypes = [];
  rfLaborComments.forEach(({ applicable_types }) => allTypes.push(applicable_types));
  return (<Fragment>
    {uniq(allTypes.flat()).map((type, index) => (<Fragment key={`master${type}${index + 1}`}>
      <hr className={theme.dividerLine} />
      <h5 className={sectionTitle}>{type}</h5>
      <ul className={styles.surfaceInfoList}>
        {rfLaborComments.filter(({ applicable_types }) => applicable_types.includes(type))
                        .map(({ comment }, index2) => (<li key={`${type}${index2 + 1}`}>
                          {comment}
                        </li>))}
      </ul>
      <br />
    </Fragment>))}
  </Fragment>);
};

const renderSurfaceInfo = (phase, surfaceName, surfaceInfo) => {
  const { sqft = null, net_square_footage = null } = phase;
  const { interested_materials: interestedMaterials = [],
          rf_labor_comments: rfLaborComments = [] } = surfaceInfo;
  const materials = interestedMaterials.length ? interestedMaterials : null;
  return (<Card className={theme.detailsElementContainer}>
    <Fragment>
      <h5 className={sectionTitle}>Surface Information</h5>
      <BreakLine />
      <div className={styles.detailsCardContainer}>
        <ResourceDetailsCardElement title="surface type" value={surfaceName} />
        <Fragment>
          <ResourceDetailsCardElement title="Project Sq Ft" value={sqft || emptyFieldValue} />
          <ResourceDetailsCardElement title="Net Sq Ft" value={net_square_footage || emptyFieldValue} />
          {!isEmpty(surfaceInfo) && <Fragment>
            <hr className={theme.dividerLine} />
            <BreakLine />
            {Object.keys(surfaceInfo).filter(key => !['interested_materials', 'rf_labor_comments'].includes(key)).map((infoKey) => {
              const title = surfaceName === 'Backsplash' ? determineBacksplashFieldName(infoKey) : infoKey.replace(/_/g, ' ');
              return (<ResourceDetailsCardElement
                key={infoKey}
                title={title}
                value={determineSurfaceDisplayValue(surfaceInfo[infoKey])}
                className={styles.resourceDetails}
              />);
            })}
            {/* keeping this, as we are not 100% on how to display rf comments */}
            {/* {rfLaborComments.length ? rfLaborComments.map((commentObj, index) => (
              <ResourceDetailsCardElement
                key={`rfComment${index + 1}`}
                title={`RF Comment ${index + 1}`}
                value={commentObj.comment || '----'}
                className={styles.resourceDetails}
              />
            )) : null} */}
            {materials && renderAddtlSurfaceInfo(materials)}
          </Fragment>}
        </Fragment>
      </div>
    </Fragment>
    {rfLaborComments.length ? renderRfInfo(rfLaborComments) : null}
  </Card>);
};

const renderPhoneNumberDisplay = (phoneNumber) => {
  const display = <PhoneLink number={phoneNumber.number} />;
  if (phoneNumber.ok_to_text) return <div>{display} - OK to Text</div>;
  return display;
};

const PhaseDetails = ({ phase, canEdit, role, surfaceType, store, purchaseOrders }) => {
  const surfaceName = surfaceType ? surfaceType.name : '----------';
  const editAction = (
    <RouterLink className={theme.detailsLink} to={`/phases/${phase.id}/edit`}>
      <span className={theme.editText}>Edit phase</span>
    </RouterLink>
  );
  const surfaceInfo = phase.surface_information || {};
  const purchaseOrdersCopy = [...purchaseOrders].filter(po => po.id >= 0);
  if (phase.type === 'Workorder') {
    purchaseOrdersCopy.push({
      id: '0',
      kind: 'Related',
      number: phase.related_po,
    });
  }
  const fwProjId = phase.floor_wizard_job ? phase.floor_wizard_job.floor_wizard_project_id : null;
  const commMethod = phase.communication_method || emptyFieldValue;
  const phaseSpecifics = phase.phase_specific;
  return (<ThemeProvider theme={detailsTheme}>
    <Card>
      <CardTitle
        title={phase.type}
        subtitle={`Created on ${phase.created_at}`}
        divider
      >{canEdit ? editAction : null}</CardTitle>
      <section className={sectionTheme.section}>
        <Card className={theme.detailsElementContainer}>
          <h5 className={sectionTitle}>Basic Information</h5>
          <BreakLine />
          <div className={styles.detailsCardContainer}>
            <ResourceDetailsCardElement title="date sold" value={phase.date_sold} />
            <ResourceDetailsCardElement title="customer name" value={phase.customer_name} />
            <ResourceDetailsCardElement
              title={`${vendorDisplayName(phase.source, phase.workroom.name)} associate`}
              value={phase.associate || '----'}
            />
            <ResourceDetailsCardElement title="street address">
              <DetailsAddress
                address={phase.street_address}
                city={phase.city}
                state={phase.state}
                zipCode={phase.zip_code}
              />
            </ResourceDetailsCardElement>
            <ResourceDetailsCardElement
              title="Year Built"
              value={phase.year_built || emptyFieldValue}
            />
            {phase.rocky_managed && <ResourceDetailsCardElement
              title="Measure Vendor"
              value="Rocky (Lowe's direct)"
            />}
            {phase.type === 'Workorder' && <Fragment>
              <ResourceDetailsCardElement
                title="Workorder Source"
                value={phase.wo_source || emptyFieldValue}
              />
              <ResourceDetailsCardElement
                title="Workorder Type"
                value={phase.wo_type || emptyFieldValue}
              />
              {/**
              * Added an extra ResourceDetailsCardElement for proper alignment of elements
              */}
              <ResourceDetailsCardElement />
            </Fragment>}
          </div>
        </Card>
        <br />
        <Card className={theme.detailsElementContainer}>
          <h5 className={sectionTitle}>Contact Information</h5>
          <BreakLine />
          <div className={styles.detailsCardContainer}>
            <ResourceDetailsCardElement
              title="Phone Number 1"
              value={phase.phone_numbers[0] ?
                renderPhoneNumberDisplay(phase.phone_numbers[0]) : emptyFieldValue}
            />
            <ResourceDetailsCardElement
              title="Phone Number 2"
              value={phase.phone_numbers[1] ?
                renderPhoneNumberDisplay(phase.phone_numbers[1]) : emptyFieldValue}
            />
            <ResourceDetailsCardElement
              title="Phone Number 3"
              value={phase.phone_numbers[2] ?
                renderPhoneNumberDisplay(phase.phone_numbers[2]) : emptyFieldValue}
            />
            <ResourceDetailsCardElement title="email address">
              {phase.email ? <EmailLink email={phase.email}>
                {phase.email}
              </EmailLink> : emptyFieldValue}
            </ResourceDetailsCardElement>
            <ResourceDetailsCardElement title="primary mode of contact" value={phase.mode_of_contact} />
            <ResourceDetailsCardElement
              title="automatic communication method"
              value={commMethod.charAt(0).toUpperCase() + commMethod.slice(1)}
            />
          </div>
        </Card>
        <br />
        <Card className={theme.detailsElementContainer}>
          <h5 className={sectionTitle}>Purchase Information</h5>
          <BreakLine />
          <div className={styles.detailsCardContainer}>
            <ResourceDetailsCardElement title="project number" value={phase.project_number || emptyFieldValue} />
            <ResourceDetailsCardElement title="P.O. numbers">
              {purchaseOrdersCopy.length ? (<PurchaseOrders all purchaseOrders={purchaseOrdersCopy} />) : '----'}
            </ResourceDetailsCardElement>
            <ResourceDetailsCardElement title="store number" value={store.number} />
            <ResourceDetailsCardElement title="spig delivery" value={phase.spig_delivery ? 'Yes' : '----'} />
            {phase.type === 'Installation' && <ResourceDetailsCardElement
              title="Product Location"
              value={phase.product_location || emptyFieldValue}
            />}
            {phase.type !== 'Assessment' && <ResourceDetailsCardElement
              title="Vendor ETA"
              value={phase.vendor_eta || emptyFieldValue}
            />}
          </div>
        </Card>
        <br />
        {renderSurfaceInfo(phase, surfaceName, surfaceInfo, (surfaceType || {}).id || null)}
        <br />
        { can(role, 'view', 'phase_specifics') && <Card className={theme.detailsElementContainer}>
          <h5 className={sectionTitle}>Phase Specifics - General</h5>
          <BreakLine />
          <div className={styles.detailsCardContainer}>
            <ResourceDetailsCardElement
              title="Customer Has Power"
              value={phaseSpecifics.customer_has_power ? 'Yes' : 'No'}
            />
            <ResourceDetailsCardElement
              title="Customer Has Climate Control"
              value={phaseSpecifics.customer_has_climate_control ? 'Yes' : 'No'}
            />
            <ResourceDetailsCardElement
              title="Customer Has Water"
              value={phaseSpecifics.customer_has_water ? 'Yes' : 'No'}
            />
            <ResourceDetailsCardElement
              title="Moving Furniture"
              value={phaseSpecifics.need_move_furniture ? 'Yes' : 'No'}
            />
            <ResourceDetailsCardElement
              title="Moving Oversized Furniture"
              value={phaseSpecifics.need_move_furniture_oversized ? 'Yes' : 'No'}
            />
            <ResourceDetailsCardElement
              title="Moving Appliances"
              value={phaseSpecifics.need_move_appliances ? 'Yes' : 'No'}
            />
            {!MILLWORK_SURFACES.includes(phase.surface_type.id) && <Fragment>
              <ResourceDetailsCardElement
                title="Subfloor Prep"
                value={phaseSpecifics.need_subfloor_prep ? 'Yes' : 'No'}
              />
              <ResourceDetailsCardElement
                title="Subfloor Replacement"
                value={phaseSpecifics.need_subfloor_replacement ? 'Yes' : 'No'}
              />
              <ResourceDetailsCardElement
                title="Installing Baseboard/Moulding"
                value={phaseSpecifics.need_install_baseboard ? 'Yes' : 'No'}
              />
              <ResourceDetailsCardElement
                title="Installing Hard Surface Transitions"
                value={phaseSpecifics.need_hardsurface_transitions ? 'Yes' : 'No'}
              />
            </Fragment>}
          </div>
          <BreakLine />
          {!!surfaceType && <Fragment>
            {MILLWORK_SURFACES.includes(phase.surface_type.id) ? <span>
              <h5 className={sectionTitle}>Phase Specifics - Millwork</h5>
              <BreakLine />
              <div className={styles.detailsCardContainer}>
                <ResourceDetailsCardElement
                  title="Number of Doors"
                  value={phaseSpecifics.millwork_specifics.num_doors}
                />
                <ResourceDetailsCardElement
                  title="Number of Windows"
                  value={phaseSpecifics.millwork_specifics.num_windows}
                />
                <ResourceDetailsCardElement
                  title="Permit Required"
                  value={phaseSpecifics.millwork_specifics.permit_required ? 'Yes' : 'No'}
                />
              </div>
            </span> : <Fragment>
              {surfaceName === 'Hardwood' && <span>
                <h5 className={sectionTitle}>Phase Specifics - Hardwood</h5>
                <BreakLine />
                <div className={styles.detailsCardContainer}>
                  <ResourceDetailsCardElement
                    title="Naildown Hardwood"
                    value={phaseSpecifics.is_naildown_hardwood ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Gluedown Hardwood"
                    value={phaseSpecifics.is_gluedown_hardwood ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Floating Hardwood"
                    value={phaseSpecifics.is_floating_hardwood ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Cali Bamboo"
                    value={phaseSpecifics.is_cali_bamboo_hardwood ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Hardwood Steps"
                    value={phaseSpecifics.is_steps_hardwood ? 'Yes' : 'No'}
                  />
                </div>
                <BreakLine />
              </span>}
              {surfaceName === 'Carpet' && <span>
                <h5 className={sectionTitle}>Phase Specifics - Carpet</h5>
                <BreakLine />
                <div className={styles.detailsCardContainer}>
                  <ResourceDetailsCardElement
                    title="Stretch In Carpet"
                    value={phaseSpecifics.is_stretch_in_carpet ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Gluedown Carpet"
                    value={phaseSpecifics.is_gluedown_carpet ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Binding Carpet"
                    value={phaseSpecifics.is_binding_carpet ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Pattern Match Carpet"
                    value={phaseSpecifics.is_pattern_match_carpet ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Berber Carpet"
                    value={phaseSpecifics.is_berber_carpet ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Carpet Steps"
                    value={phaseSpecifics.is_steps_carpet ? 'Yes' : 'No'}
                  />
                </div>
                <BreakLine />
              </span>}
              {['Tile', 'Backsplash'].includes(surfaceName) && <span>
                <h5 className={sectionTitle}>Phase Specifics - Tile/Backsplash</h5>
                <BreakLine />
                <div className={styles.detailsCardContainer}>
                  <ResourceDetailsCardElement
                    title="Floor Tile"
                    value={phaseSpecifics.is_floor_tile ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Patterns Tile"
                    value={phaseSpecifics.is_patterns_tile ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Large Format Tile"
                    value={phaseSpecifics.is_large_format_tile ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Backsplash Tile"
                    value={phaseSpecifics.is_backsplash_tile ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Glass Backsplash"
                    value={phaseSpecifics.is_backsplash_glass ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Backsplash Blocking"
                    value={phaseSpecifics.is_backsplash_blocking ? 'Yes' : 'No'}
                  />
                </div>
                <BreakLine />
              </span>}
              {['Vinyl', 'Vinyl Plank'].includes(surfaceName) && <span>
                <h5 className={sectionTitle}>Phase Specifics - Vinyl</h5>
                <BreakLine />
                <div className={styles.detailsCardContainer}>
                  <ResourceDetailsCardElement
                    title="Sheet Vinyl"
                    value={phaseSpecifics.is_vinyl_sheet ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Vinyl Tile"
                    value={phaseSpecifics.is_vct_lvt ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Gluedown VP"
                    value={phaseSpecifics.is_gluedown_vinyl_plank ? 'Yes' : 'No'}
                  />
                  <ResourceDetailsCardElement
                    title="Floating VP"
                    value={phaseSpecifics.is_floating_vinyl_plank ? 'Yes' : 'No'}
                  />
                </div>
                <BreakLine />
              </span>}
              <h5 className={sectionTitle}>Phase Specifics - Removal</h5>
              <BreakLine />
              <div className={styles.detailsCardContainer}>
                <ResourceDetailsCardElement
                  title="Removing/Replacing Toilet"
                  value={phaseSpecifics.need_remove_replace_toilet ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  title="Removing Naildown Hardwood"
                  value={phaseSpecifics.need_remove_naildown_hardwood ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  title="Removing Floating Hardwood"
                  value={phaseSpecifics.need_remove_floating_hardwood ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  title="Removing Gluedown Hardwood"
                  value={phaseSpecifics.need_remove_gluedown_hardwood ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  title="Removing Carpet Over Pad"
                  value={phaseSpecifics.need_remove_over_pad_carpet ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  title="Removing Gluedown Carpet"
                  value={phaseSpecifics.need_remove_gluedown_carpet ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  title="Removing Laminate"
                  value={phaseSpecifics.need_remove_laminate ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  title="Removing Tile"
                  value={phaseSpecifics.need_remove_tile ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  title="Removing Gluedown Vinyl"
                  value={phaseSpecifics.need_remove_gluedown_vinyl ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  title="Removing Vinyl Plank"
                  value={phaseSpecifics.need_remove_vinyl_plank ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  title="Remove Backsplash"
                  value={phaseSpecifics.need_remove_backsplash ? 'Yes' : 'No'}
                />
              </div>
            </Fragment>}
          </Fragment>}
        </Card>}
        <br />
        {role !== 'installer' && <Card className={theme.detailsElementContainer}>
          <h5 className={sectionTitle}>Additional Information</h5>
          <BreakLine />
          <div className={styles.detailsCardContainer}>
            {phase.type === 'Installation' && <ResourceDetailsCardElement
              title="Haul Away and Removal"
              className={styles.additionalComments}
              value={phase.haul_away || emptyFieldValue}
              columns="desktop-1x"
            />}
            {phase.type === 'Installation' && <ResourceDetailsCardElement
              title="Additional Work"
              className={styles.additionalComments}
              value={phase.additional_work || emptyFieldValue}
              columns="desktop-1x"
            />}
            <ResourceDetailsCardElement
              title="Additional Comments"
              className={styles.additionalComments}
              value={phase.additional_comments || emptyFieldValue}
              columns="desktop-1x"
            />
            {phase.type === 'Assessment' && <ResourceDetailsCardElement
              title="Floor Wizard Project ID"
              className={styles.additionalComments}
              value={fwProjId || emptyFieldValue}
              columns="desktop-1x"
            />}
          </div>
          </Card>}
      </section>
    </Card>
  </ThemeProvider>);
};

PhaseDetails.propTypes = {
  phase: PhaseType.isRequired,
  canEdit: PropTypes.bool.isRequired,
  role: PropTypes.string.isRequired,
  surfaceType: PropTypes.shape({ name: PropTypes.string }),
  store: PropTypes.shape({ number: PropTypes.string }),
  purchaseOrders: PropTypes.arrayOf(PropTypes.shape(purchaseOrderShape)),
};

const mapStateToProps = (state) => {
  const phase = state.phase;
  const surfaceType = (phase.details && phase.details.surface_type) || {};
  const store = phase.details && phase.details.store;
  return {
    purchaseOrders: phase.purchaseOrders.items,
    phase: phase.details,
    store,
    surfaceType,
    canEdit: can(getRole(state), 'update', 'phase'),
    role: getRole(state),
  };
};

export default connect(mapStateToProps)(PhaseDetails);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/phaseDetails/index.jsx